import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';
import { FileDown, Copy, ExternalLink, RefreshCw, CheckCircle, AlertCircle, Info, Terminal } from 'lucide-react';

function XmlExportSettings() {
  const [loading, setLoading] = useState(false);
  const [generatingXml, setGeneratingXml] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const [feedUrl, setFeedUrl] = useState<string>('');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [exportCount, setExportCount] = useState<number>(0);

  useEffect(() => {
    fetchSettings();

    // Costruisci l'URL del feed
    const url = `${window.location.origin}/.netlify/functions/property-feed`;
    setFeedUrl(url);
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      // Fetch settings to get last update time
      const { data: settingsData, error: settingsError } = await supabase
        .from('settings')
        .select('xml_feed_last_update, xml_feed_status')
        .eq('id', '00000000-0000-0000-0000-000000000000')
        .single();

      if (settingsError) {
        console.error('Error fetching settings:', settingsError);
      } else if (settingsData) {
        setLastUpdate(settingsData.xml_feed_last_update);
        setStatusMessage(settingsData.xml_feed_status || '');
      }

      // Get count of publishable properties
      const { data: publishedStatus } = await supabase
        .from('property_statuses')
        .select('id')
        .eq('name', 'published')
        .single();

      if (publishedStatus) {
        const { count, error: countError } = await supabase
          .from('properties')
          .select('*', { count: 'exact', head: true })
          .eq('status_id', publishedStatus.id);

        if (countError) {
          console.error('Error counting properties:', countError);
        } else {
          setExportCount(count || 0);
        }
      }
    } catch (error) {
      console.error('Error in fetchSettings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateXml = async () => {
    setGeneratingXml(true);
    setStatusMessage('Generazione del tracciato XML in corso...');
    
    try {
      // Genera il timestamp per evitare problemi di cache
      const timestamp = Date.now();
      
      // Mostra un messaggio di caricamento persistente
      const toastId = toast.loading('Generazione XML in corso...', { autoClose: false });
      
      // Chiama la Netlify Function con il flag force=true per forzare la rigenerazione
      const response = await fetch(`/.netlify/functions/property-feed?t=${timestamp}&force=true`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Errore HTTP ${response.status}: ${errorText}`);
      }
      
      const xmlContent = await response.text();
      
      // Conta gli immobili inclusi
      const count = (xmlContent.match(/<annuncio>/g) || []).length;
      
      // Aggiorna lo stato locale
      setLastUpdate(new Date().toISOString());
      setStatusMessage(`Tracciato XML generato con successo. ${count} immobili inclusi.`);
      setExportCount(count);
      
      // Mostra anteprima
      
      // Chiama la funzione generate-xml-file per salvare il file fisico
      const generateResponse = await fetch(`/.netlify/functions/generate-xml-file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        },
        body: JSON.stringify({ xml: xmlContent })
      });
      
      if (!generateResponse.ok) {
        console.warn(`Avviso: salvataggio file XML fallito: ${generateResponse.status}`);
        toast.update(toastId, {
          render: `XML generato ma non salvato su filesystem. ${count} immobili inclusi.`,
          type: toast.TYPE.WARNING,
          autoClose: 5000,
          isLoading: false
        });
      } else {
        toast.update(toastId, {
          render: `Tracciato XML generato con successo! ${count} immobili inclusi.`,
          type: toast.TYPE.SUCCESS,
          autoClose: 3000,
          isLoading: false
        });
      }
      
      // Aggiorna anche il file pubblico XML per forzare l'aggiornamento della cache
      await fetch(`/property-feed.xml?t=${timestamp}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      // Ricarica la pagina per vedere i cambiamenti
      await fetchSettings();
    } catch (error) {
      console.error('Error generating XML:', error);
      const errorMessage = error instanceof Error ? error.message : 'Errore sconosciuto';
      setStatusMessage(`Errore: ${errorMessage}`);
      
      toast.error(`Errore: ${errorMessage}`);
    } finally {
      setGeneratingXml(false);
    }
  };

  const copyFeedUrl = () => {
    // Apri il feed con un timestamp per evitare la cache
    const timestamp = Date.now();
    const urlWithTimestamp = `${feedUrl}?t=${timestamp}`;
    navigator.clipboard.writeText(urlWithTimestamp);
    toast.success('URL copiato negli appunti');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Mai';
    return new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const viewXmlFeed = () => {
    // Apri il feed con un timestamp per evitare la cache
    const timestamp = Date.now();
    window.open(`/.netlify/functions/property-feed?t=${timestamp}`, '_blank');
  };

  return (
    <AdminLayout title="Esportazione XML Portali">
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Tracciato XML Portali Immobiliari</h2>
        <p className="text-gray-600">Gestisci l'esportazione automatica degli immobili verso i portali immobiliari</p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          </div>
        ) : (
          <>
            <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
              <div className="bg-primary/5 p-4 rounded-lg flex items-center flex-grow">
                <div className="flex-grow">
                  <h4 className="font-medium text-primary">Stato Esportazione</h4>
                  <p className="text-sm text-gray-600">
                    {exportCount} immobili pubblicati pronti per l'esportazione
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-sm text-gray-500 block">Ultimo aggiornamento:</span>
                  <span className="font-medium">{formatDate(lastUpdate)}</span>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button 
                  onClick={handleGenerateXml}
                  disabled={generatingXml}
                  className="bg-primary hover:bg-primary-light text-white py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {generatingXml ? (
                    <>
                      <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                      Generazione...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-5 w-5 mr-2" />
                      Genera XML
                    </>
                  )}
                </button>
                
                <button 
                  onClick={viewXmlFeed}
                  className="bg-secondary hover:bg-secondary-light text-white py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center whitespace-nowrap"
                >
                  <FileDown className="h-5 w-5 mr-2" />
                  Visualizza
                </button>
              </div>
            </div>

            {statusMessage && (
              <div className={`p-4 mb-6 rounded-lg ${statusMessage.includes('Errore') ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>
                <div className="flex items-start">
                  {statusMessage.includes('Errore') ? (
                    <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                  ) : (
                    <CheckCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                  )}
                  <p>{statusMessage}</p>
                </div>
              </div>
            )}

            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                  <Info className="h-5 w-5 mr-2 text-gray-500" />
                  Informazioni sul feed XML
                </h4>
                <div className="flex">
                  <input
                    type="text"
                    value={`${feedUrl}?t=${Date.now()}`}
                    readOnly
                    className="flex-grow border border-gray-300 rounded-l-lg px-3 py-2 bg-gray-50"
                  />
                  <button
                    onClick={copyFeedUrl}
                    className="bg-secondary hover:bg-secondary-light text-white px-3 py-2 rounded-r-lg transition-colors"
                    title="Copia URL"
                  >
                    <Copy className="h-5 w-5" />
                  </button>
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Questo URL fornisce l'accesso al tracciato XML di tutti gli immobili pubblicati. Può essere configurato nei portali immobiliari per il caricamento automatico.
                </p>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-start">
                  <Info className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <h5 className="font-medium text-blue-700 mb-1">Informazioni sul Tracciato XML</h5>
                    <p className="text-sm text-blue-600">
                      Il feed contiene solo gli immobili con stato "Pubblicato" e include foto, descrizioni, dettagli e riferimenti secondo lo standard per portali immobiliari.
                    </p>
                    <div className="mt-2 flex gap-3">
                      <a 
                        href={`${feedUrl}?t=${Date.now()}`} 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-700 hover:text-blue-800 font-medium text-sm"
                      >
                        Visualizza XML
                        <ExternalLink className="h-4 w-4 ml-1" />
                      </a>
                      <a 
                        href={`${feedUrl}?force=true&t=${Date.now()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-700 hover:text-blue-800 font-medium text-sm"
                      >
                        Forza aggiornamento
                        <RefreshCw className="h-4 w-4 ml-1" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

export default XmlExportSettings;